// What a thing on the island IS, for the tap-to-ask caption on the table.
// Pure: a placement (from layout.js) plus the civ in, a title and a line
// out. Playtest 2: "I should be able to click on the building and have it
// tell me what it does."

import {
  REGIONS, TECHS, DIRECTIONS, GOALS, goalOf,
} from '@/engine/cards';
import { BUILDINGS } from '@/engine/city';
import { FINDS } from '@/engine/terrain';
import { wayText } from '@/engine/rivals';
import { UNHAPPY_AT, CITY } from '@/engine/constants';

const TERRAIN = {
  grass: 'Grassland', 'grass-forest': 'Forest', 'grass-hill': 'Hills', dirt: 'Bare earth', sand: 'The shore',
  'sand-rocks': 'The shore', stone: 'Stony ground', 'stone-hill': 'Stony hills', 'stone-mountain': 'A mountain',
  'stone-rocks': 'Broken stone', water: 'The sea', 'water-rocks': 'Shoals', 'water-island': 'A rocky islet',
};

export const captionFor = (placement, civ = {}) => {
  if (!placement) return null;
  const { id, model } = placement;
  const [kind, rest, extra] = id.split(':');
  const pop = civ.population ?? 0;

  // The town's fabric (2026-09-25): the wall, its towers, the roads, the square.
  if (kind === 'townwall') return { title: model === 'town:gate' ? 'The town gate' : 'The town wall', text: 'It runs round the city\'s outer edge and grows as the Stewards take in land.' };
  if (kind === 'towntower') return { title: 'A wall tower', text: 'One at every corner of the town wall.' };
  if (kind === 'highway') return { title: 'A road', text: 'Laid by the Frontier. Units go further along it, and a village joined to the city by road sends more.' };
  if (kind === 'road') return { title: 'A road', text: 'Paving from the square out to every part of the city.' };
  if (kind === 'square') return { title: 'The square', text: 'The heart of the town, round the great work: a fountain, the stalls, lanterns.' };
  if (kind === 'house') {
    return {
      title: 'A household',
      text: `${pop} ${pop === 1 ? 'person lives' : 'people live'} here. Each turns out a worker at the start of every round - half of them if happiness is ${UNHAPPY_AT} or below - and eats half a food at the end.`,
    };
  }
  if (kind === 'city') {
    const b = (civ.city?.buildings || []).find((x) => x.id === rest);
    const spec = b && BUILDINGS[b.type];
    if (!spec) return { title: 'A building', text: 'Part of the Stewards\' city.' };
    return {
      title: b.burned ? `A burned ${spec.name.toLowerCase()}` : `A ${spec.name.toLowerCase()}`,
      text: b.burned ? 'Raiders got to it. It makes nothing until the Stewards repair it.' : `${spec.text} ${spec.slots ? `Takes ${spec.slots} ${spec.slots === 1 ? 'shift' : 'shifts'} a day.` : ''}`,
    };
  }
  if (kind === 'market') {
    return { title: 'A market', text: 'Where goods become coin. Part of the Stewards\' city.' };
  }
  if (kind === 'library') {
    return {
      title: 'A library',
      text: `${civ.libraries ?? 0} built. Every library gives the Scholars one more experiment a day and one more Study. The Stewards build them.`,
    };
  }
  if (kind === 'monument') {
    const goal = GOALS[goalOf(civ)];
    const raised = (civ.wonders || []).map((w) => GOALS[w]?.name).filter(Boolean);
    if (rest === 'trees' || !goal?.build) {
      return {
        title: raised.length ? `The heart of town - ${raised.join(', ')} stand here` : 'The heart of town',
        text: 'The great work rises here when the Vision card turns and the group chooses a wonder to raise. A finished wonder gives the town something for good.',
      };
    }
    const stages = civ.monument?.stages ?? 0;
    return {
      title: `${goal.name}, stage ${stages} of ${goal.build.stages}`,
      text: `Any role may spend a turn raising the next stage (${Object.entries(goal.build.cost).map(([k, n]) => `${n} ${k}`).join(', ')}; the tree makes it cheaper). When it stands: ${goal.text}`,
    };
  }
  if (kind === 'rival') {
    const rv = (civ.rivals || []).find((x) => x.id === rest);
    if (!rv) return { title: 'Another people', text: 'Somebody else lives here.' };
    const temper = { hostile: 'They are hostile: they send raiders and make demands. Parley with coin, send an Envoy, or break them.', wary: 'They are wary: watching, and taking ground. A gift or an envoy warms them; a raid makes them hostile.', friendly: 'They are friendly: carts come with goods and coin, and their scholars share what they know.' }[rv.stance];
    return {
      title: `${rv.name.replace(/^the /, 'The ')}: ${rv.stance}, strength ${rv.power}${rv.level ? `, level ${rv.level}` : ''}`,
      text: `${wayText(rv)} ${temper} Strength is how hard they are to fight. ${(rv.held || []).length} ${(rv.held || []).length === 1 ? 'hex' : 'hexes'} of the island are theirs; a find on ground they take is gone.`,
    };
  }
  if (kind === 'tech') {
    const tech = TECHS[rest];
    return { title: tech ? tech.name : 'A technology', text: tech ? tech.text : '' };
  }
  if (kind === 'menace') {
    const m = (civ.menaces || []).find((x) => x.id === rest);
    const what = m?.kind === 'den' ? 'A bandits\' den' : m?.kind === 'wolves' ? 'A wolves\' den' : 'A raider camp';
    return {
      title: `${what}, strength ${m?.strength ?? '?'}`,
      // Playtest 9: "it doesn't really explain what the structure is... it
      // just looks like some posts".
      text: 'It sends a raider every third night, two out at most, and the longer it stands the stronger they are. Walk a warband beside it: fight it, or parley - coin, and it breaks up.',
    };
  }
  if (kind === 'find') {
    const f = (civ.finds || []).find((x) => x.id === rest);
    const what = f && FINDS[f.kind];
    return {
      title: what ? what.name : 'Something out there',
      text: `${what ? what.text : 'Worth a walk.'} Whoever walks a unit onto it brings it home.`,
    };
  }
  if (kind === 'raider') {
    const x = (civ.raiders || []).find((y) => y.id === rest);
    return {
      title: `Raiders, strength ${x?.strength ?? '?'}`,
      text: 'Walking at the nearest settlement, or at the city if there is nothing closer. At a village they wreck it - three of them burn it down. At the city they fire a building, and if nothing is left standing the town is sacked. A unit in their way stops them.',
    };
  }
  if (kind === 'village') {
    const v = (civ.villages || []).find((x) => x.id === rest);
    // A raised city kept the town's caption (playtest 2026-10-20, Ashby).
    if (v?.city) {
      const district = CITY.districts[v.district];
      return {
        title: `${v.name}, a city`,
        text: `Raised by the Stewards from a town. It grows to size ${CITY.maxSize}, rolls ${CITY.dice} dice against raiders, eats ${CITY.upkeepFood} food a night, and units can muster here.${district ? ` Its ${district.name}: ${district.text}.` : ''}`,
      };
    }
    if (v?.chartered) {
      return {
        title: `${v.name}, a town`,
        text: 'Chartered by the Hall. It fills a standing order every night without anyone tending it, units can muster here, and it eats a food a night.',
      };
    }
    return {
      title: v ? v.name : 'A village',
      text: 'Planted by a pioneer. It sends up whatever this ground makes, every night, with nobody managing it. A caravan from the city loads up and feeds it so it grows; the Hall can charter it into a town.',
    };
  }
  if (kind === 'unit') {
    const u = (civ.units || []).find((x) => x.id === rest);
    return {
      title: u ? `${u.kind === 'warband' ? 'A warband' : u.kind === 'scout' ? 'A scout' : 'A pioneer'}, strength ${u.strength}` : 'A unit',
      text: 'One of the Frontier\'s pieces. A worker is one unit\'s turn: move it, fight with it, or have it work the ground. Its strength is its dice and its hit points at once.',
    };
  }
  if (kind === 'ship') return { title: 'Boats', text: 'The commonwealth fishes. Scenery, for now.' };
  if (kind === 'cloud') {
    const name = DIRECTIONS[Number(rest)] || 'that way';
    return {
      title: `Unexplored, to the ${name}`,
      text: 'Unknown ground, as far as anyone knows. One of the Frontier\'s units stepping onto it raises whatever is here: a bonus now, and a card in the deck you will meet later.',
    };
  }
  if (kind === 'region') {
    const region = REGIONS[rest];
    if (!region) return null;
    return {
      title: region.name,
      text: `${region.text} Found by the Frontier. It paid ${describe(region.bonus)} and put a card in the deck.`,
    };
  }
  if (kind === 'tile') {
    const name = TERRAIN[model] || 'The island';
    return { title: name, text: extra === undefined && rest === '0,0' ? 'The centre of the commonwealth.' : 'Part of the island the commonwealth sits on.' };
  }
  return null;
};

const describe = (bonus = {}) => Object.entries(bonus).map(([k, n]) => `+${n} ${k}`).join(', ');
